import { Flame } from "lucide-react"
import { useTranslation } from "react-i18next"

import { CrewRosterMedallions } from "@/components/creator/crew-roster-medallions"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { useInitials } from "@/hooks/use-initials"
import type { CrewRosterMember } from "@/types/crew-hub"

/**
 * OKS-474 — §18 roster cell (UX Spec §2.5).
 *
 * A single teammate: avatar (initials fallback when `avatar_url` is null),
 * first-name (truncated single line), a streak chip (`Flame` + day count) that
 * is HIDDEN at 0 (KD-5 / UX Spec §3), and — LAST, so identity then activity
 * then prestige, and so everything above it keeps a uniform offset whatever
 * the badge count — the OKS-970 medallion strip, which renders nothing at all
 * for a crewmate holding no badges. Inert — faces are social proof, so
 * the cell does NOT navigate in v1 (no public crew-profile route exists yet,
 * PRD Q3). Min size ≥44px for the mobile touch-target rule even though it's
 * non-interactive (keeps the grid rhythm consistent if it later links).
 */
interface CrewRosterCellProps {
  member: CrewRosterMember
}

export function CrewRosterCell({ member }: CrewRosterCellProps) {
  const { t } = useTranslation()
  const initials = useInitials()
  const hasStreak = member.streak_days > 0

  return (
    <li
      data-testid="crew-roster-cell"
      className="flex w-16 min-w-11 flex-col items-center gap-1 text-center"
    >
      <Avatar className="size-12">
        {member.avatar_url && <AvatarImage src={member.avatar_url} alt="" />}
        <AvatarFallback className="text-sm">
          {initials(member.first_name)}
        </AvatarFallback>
      </Avatar>
      <span
        className="w-full truncate text-xs font-medium"
        title={member.first_name}
      >
        {member.first_name}
      </span>
      {hasStreak && (
        <Badge
          variant="secondary"
          data-testid="crew-roster-streak"
          className="gap-0.5 px-1.5 py-0 text-[10px] tabular-nums"
        >
          <Flame aria-hidden="true" className="size-3" />
          {t("crew_hub.roster.streak", { count: member.streak_days })}
        </Badge>
      )}
      <CrewRosterMedallions
        badges={member.badges}
        firstName={member.first_name}
      />
    </li>
  )
}
